import {
    FC,
    ImgHTMLAttributes,
    ReactElement,
    useEffect,
    useState,
} from 'react';
import { Nullable } from 'primereact/ts-helpers';
import styles from './Avatar.module.scss';
import classNames from 'classnames';
import { Skeleton } from 'antd';
import { useGetProfileImageQuery } from 'src/api/endpoints/profile';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetUserProfileImageQuery } from 'src/api/endpoints/users';

export type AvatarProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
    src?: Nullable<string>;
    userId?: number;

    // Default 30px
    width?: string | number;

    // Default 30px
    height?: string | number;

    size?: string | number;

    // Default 50%
    radius?: string | number;

    isLoading?: boolean;
};

export const Avatar: FC<AvatarProps> = (props: AvatarProps): ReactElement => {
    const {
        className,
        alt,
        radius = '50%',
        width = '30px',
        height = '30px',
        userId,
        size,
        ...rest
    } = props;

    const [isCorrect, setCorrect] = useState<boolean>(true);

    const {
        data: selfProfileImage,
        isFetching: isSelfProfileImageFetching,
        isError: isSelfProfileImageError,
    } = useGetProfileImageQuery(userId ? skipToken : undefined);

    const {
        data: userProfileImage,
        isFetching: isUserProfileImageFetching,
        isError: isUserProfileImageError,
    } = useGetUserProfileImageQuery(
        userId ? { params: { userId } } : skipToken
    );

    useEffect(() => {
        let correct: boolean = true;

        if (isSelfProfileImageError || isUserProfileImageError) correct = false;

        setCorrect(correct);
    }, [isSelfProfileImageError, isUserProfileImageError]);

    if (isSelfProfileImageFetching || isUserProfileImageFetching) {
        return (
            <Skeleton.Avatar
                active={true}
                style={{
                    borderRadius: radius,
                    width: size ?? width,
                    height: size ?? height,
                }}
            />
        );
    }

    if ((userProfileImage || selfProfileImage) && isCorrect) {
        return (
            <img
                width={size ?? width}
                height={size ?? height}
                className={classNames(styles.avatar, className)}
                style={{
                    borderRadius: radius,
                }}
                alt={alt}
                {...rest}
                src={`data:image/png;base64, ${userProfileImage ? userProfileImage.base : selfProfileImage?.base}`}
            />
        );
    }

    return (
        <svg
            fill="#edf1f7"
            version="1.1"
            id="Capa_1"
            xmlns="http://www.w3.org/2000/svg"
            width={size ?? width}
            height={size ?? height}
            style={{
                minWidth: size ?? width,
                minHeight: size ?? height,
            }}
            viewBox="0 0 45.532 45.532"
            className={classNames(styles.avatar, className)}
        >
            <g>
                <path
                    d="M22.766,0.001C10.194,0.001,0,10.193,0,22.766s10.193,22.765,22.766,22.765c12.574,0,22.766-10.192,22.766-22.765
		S35.34,0.001,22.766,0.001z M22.766,6.808c4.16,0,7.531,3.372,7.531,7.53c0,4.159-3.371,7.53-7.531,7.53
		c-4.158,0-7.529-3.371-7.529-7.53C15.237,10.18,18.608,6.808,22.766,6.808z M22.761,39.579c-4.149,0-7.949-1.511-10.88-4.012
		c-0.714-0.609-1.126-1.502-1.126-2.439c0-4.217,3.413-7.592,7.631-7.592h8.762c4.219,0,7.619,3.375,7.619,7.592
		c0,0.938-0.41,1.829-1.125,2.438C30.712,38.068,26.911,39.579,22.761,39.579z"
                />
            </g>
        </svg>
    );
};
